function cargarImagen(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo leer la imagen seleccionada.'));
    img.src = url;
  });
}

/** Chrome/Android: algunos formatos decodifican mejor con createImageBitmap que con <img>. */
async function decodificarArchivoImagen(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (_e) {
      /* seguir con <img> */
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    return await cargarImagen(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function canvasABlobWebp(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('No se pudo procesar la imagen en formato WebP.'));
      },
      'image/webp',
      quality
    );
  });
}

/**
 * Convierte una imagen a WebP y fuerza un tamaño final bajo (maxOutputBytes).
 * Estrategia: baja calidad y luego resolución progresivamente.
 */
export async function prepararImagenWebp({
  file,
  maxInputBytes,
  maxSidePx,
  maxOutputBytes,
  outputNameBase,
  emptyFileMessage,
  tooLargeMessage,
  invalidTypeMessage,
}) {
  if (!file?.size) throw new Error(emptyFileMessage);
  if (file.size > maxInputBytes) throw new Error(tooLargeMessage);
  if (!file.type.startsWith('image/')) throw new Error(invalidTypeMessage);

  const img = await decodificarArchivoImagen(file);
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  if (w0 < 2 || h0 < 2) throw new Error('La imagen está dañada o es demasiado chica.');

  let scaleFactor = Math.min(1, maxSidePx / Math.max(w0, h0));
  let quality = 0.76;
  let attempts = 0;
  let bestBlob = null;

  while (attempts < 8) {
    attempts += 1;
    const w = Math.max(1, Math.round(w0 * scaleFactor));
    const h = Math.max(1, Math.round(h0 * scaleFactor));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await canvasABlobWebp(canvas, quality);
    if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
    if (blob.size <= maxOutputBytes) {
      const nombre = `${outputNameBase}.webp`;
      return new File([blob], nombre, { type: 'image/webp' });
    }

    if (quality > 0.48) {
      quality = Math.max(0.48, quality - 0.08);
    } else {
      scaleFactor *= 0.86;
    }
  }

  if (!bestBlob) throw new Error('No se pudo procesar la imagen.');
  if (bestBlob.size > maxOutputBytes) {
    throw new Error(
      `No se pudo reducir la imagen por debajo de ${Math.round(
        maxOutputBytes / 1024
      )} KB. Probá con una foto más chica.`
    );
  }
  const nombre = `${outputNameBase}.webp`;
  return new File([bestBlob], nombre, { type: 'image/webp' });
}
